/**
 * Template 3: Social Proof Showcase
 *
 * Trust-building template with testimonials and stats.
 * Target: Building credibility, social proof, reviews-focused
 *
 * Two-Phase Animation Timeline (15 seconds @ 60fps = 900 frames):
 *
 * PHASE 1: Social Proof Showcase (0-7.5s / frames 0-225)
 * - 0.0-0.5s (0-15):    Customer count badge fade in
 * - 0.5-1.5s (15-45):   Product image scale reveal
 * - 1.5-2.5s (45-75):   Product title & price
 * - 2.5-5.0s (75-150):  Testimonial quote typing
 * - 5.0-6.0s (150-180): Customer name fade in
 * - 6.0-7.0s (180-210): Star rating animation
 * - 7.0-7.5s (210-225): Fade out transition
 *
 * PHASE 2: Caption Scene (7.5-15s / frames 225-450)
 * - 7.5-8.0s (225-240):  Scene fade in
 * - 8.0-12.0s (240-360): Caption typewriter
 * - 12.0-15s (360-450):  Hold with swipe-up indicator
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import type { SocialProofShowcaseProps, VisualEffectsConfig } from '../types';
import { DEFAULT_VISUAL_EFFECTS, FONT_ID_TO_FAMILY } from '../types';
import { ProductImage } from '../components/ProductImage';
import { AnimatedText } from '../components/AnimatedText';
import { StarRating } from '../components/StarRating';
import { CaptionScene } from '../components/CaptionScene';
import { TemplateBackground } from '../components/TemplateBackground';
import { SparkleCluster } from '../components/Sparkles';
import { ShimmerEffect } from '../components/ShimmerEffect';
import { PRICE_DISCLAIMER } from '../components/AnimatedPrice';
import { DiscountBadge } from '../components/DiscountBadge';

export const SocialProofShowcase: React.FC<SocialProofShowcaseProps> = ({
  productTitle,
  productImage,
  salePrice,
  testimonialQuote,  // Optional - no default (prevents fabricated quotes)
  customerName,      // Optional - no default
  rating,            // Optional - only shown if >= 4.0 AND reviewCount >= 5
  reviewCount,       // Optional - requires minimum 5 to display rating
  customerCount,     // Optional - no default (prevents fabricated numbers)
  discountCode,
  discountLabel,
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  effects: effectsOverride,
  backgroundType = 'aurora',
  backgroundPalette = 'midnight',
  textAnimation = 'typewriter',
  language = 'de',
  showPriceDisclaimer = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Merge user effects with defaults
  const effects: VisualEffectsConfig = {
    ...DEFAULT_VISUAL_EFFECTS,
    ...effectsOverride,
  };

  // Resolve font family from font ID (for Phase 1 text components)
  const fontFamily = FONT_ID_TO_FAMILY[captionFont] || 'Inter, sans-serif';

  // Phase transition point
  const PHASE_TRANSITION = fps * 7.5; // Frame 225

  // Phase 1: Timeline markers (compressed to 7.5s)
  const CUSTOMER_COUNT_START = 0;
  const PRODUCT_START = fps * 0.5;
  const TITLE_START = fps * 1.5;
  const QUOTE_START = fps * 2.5;
  const NAME_START = fps * 5;
  const RATING_START = fps * 6;

  // Phase 1 fade out
  const phase1Opacity = interpolate(
    frame,
    [PHASE_TRANSITION - fps * 0.5, PHASE_TRANSITION],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Customer count animation
  const customerCountOpacity = interpolate(
    frame,
    [CUSTOMER_COUNT_START, CUSTOMER_COUNT_START + fps * 0.5],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
  const customerCountY = interpolate(
    frame,
    [CUSTOMER_COUNT_START, CUSTOMER_COUNT_START + fps * 0.5],
    [-20, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Title and price fade in
  const titleOpacity = interpolate(
    frame,
    [TITLE_START, TITLE_START + fps * 0.4],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Customer name fade in
  const nameOpacity = interpolate(
    frame,
    [NAME_START, NAME_START + fps * 0.4],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Determine if we're in Phase 1 or Phase 2
  const isPhase1 = frame < PHASE_TRANSITION;
  const isPhase2 = frame >= PHASE_TRANSITION - fps * 0.5;

  // Brand color for backgrounds (trust green theme)
  const brandColor = '#1a2e1a';
  const accentColor = '#4CAF50';

  // Phase 1 content (shared across all background types)
  const phase1Content = (
    <>
      <AbsoluteFill
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 50px',
          opacity: phase1Opacity,
        }}
      >
        {/* Customer Count Badge - only shown if customerCount is provided (prevents fabricated numbers) */}
        {customerCount && customerCount > 0 && (
          <div
            style={{
              opacity: customerCountOpacity,
              transform: `translateY(${customerCountY}px)`,
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              padding: '16px 44px',
              borderRadius: 28,
              marginBottom: 30,
              border: '2px solid rgba(76, 175, 80, 0.3)',
            }}
          >
            <AnimatedText
              text={`${customerCount.toLocaleString()}+ happy customers`}
              animationType={textAnimation}
              startFrame={CUSTOMER_COUNT_START}
              color={captionColor}
              fontSize={36}
              fontWeight={600}
              fontFamily={fontFamily}
              textAlign="center"
              wordDelay={4}
            />
          </div>
        )}

        {/* Product Image with Sparkles */}
        <div style={{ position: 'relative', marginBottom: 30 }}>
          <ProductImage
            src={productImage}
            alt={productTitle}
            startFrame={PRODUCT_START}
            width={520}
            height={520}
            borderRadius={24}
            shadow="0 30px 80px rgba(0,0,0,0.4)"
            entranceAnimation="fadeScale"
            enableFloat={true}
            floatAmplitude={4}
          />

          {/* Sparkles around the product (trust green & gold) */}
          {effects.sparkles && (
            <>
              <SparkleCluster
                centerX={50}
                centerY={50}
                radius={45}
                count={16}
                color="rgba(255, 255, 255, 0.9)"
                startFrame={PRODUCT_START + fps * 0.8}
                seed={55555}
              />
              {/* Gold sparkles for premium feel */}
              <SparkleCluster
                centerX={85}
                centerY={15}
                radius={12}
                count={8}
                color="#FFD700"
                startFrame={PRODUCT_START + fps * 1.2}
                seed={66666}
              />
            </>
          )}
        </div>

        {/* Product Title & Price (uses selected text animation) */}
        <div
          style={{
            opacity: titleOpacity,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              marginBottom: 12,
              maxWidth: 800,
            }}
          >
            <AnimatedText
              text={productTitle}
              animationType={textAnimation}
              startFrame={TITLE_START}
              color={captionColor}
              fontSize={50}
              fontWeight={700}
              fontFamily={fontFamily}
              textAlign="center"
              wordDelay={4}
            />
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <AnimatedText
              text={salePrice}
              animationType={textAnimation}
              startFrame={TITLE_START + fps * 0.3}
              color={captionColor}
              fontSize={60}
              fontWeight={700}
              fontFamily={fontFamily}
              textAlign="center"
              wordDelay={4}
            />

            {/* Shimmer effect over the price */}
            {effects.priceShimmer && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: -20,
                  right: -20,
                  bottom: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                <ShimmerEffect
                  width={100}
                  color="rgba(76, 175, 80, 0.4)"
                  delay={fps * 0.5}
                  duration={35}
                  repeatCount={2}
                  repeatGap={fps * 2}
                  angle={-20}
                  intensity={0.7}
                  startFrame={TITLE_START}
                  blur={12}
                />
              </div>
            )}
          </div>

          {/* PAngV Price Disclaimer */}
          {showPriceDisclaimer && (
            <div
              style={{
                marginTop: 8,
                opacity: titleOpacity,
              }}
            >
              <span
                style={{
                  fontSize: 21,
                  color: 'rgba(255,255,255,0.6)',
                  fontFamily,
                }}
              >
                {PRICE_DISCLAIMER[language] || PRICE_DISCLAIMER.de}
              </span>
            </div>
          )}
        </div>

        {/* Testimonial Quote Box - only shown if testimonialQuote is provided (prevents fabricated quotes) */}
        {testimonialQuote && testimonialQuote.trim() !== '' && (
          <div
            style={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              padding: '22px 32px',
              borderRadius: 16,
              borderLeft: '5px solid #4CAF50',
              maxWidth: 720,
              marginBottom: 24,
            }}
          >
            {/* Quote marks */}
            <span
              style={{
                color: '#4CAF50',
                fontSize: 52,
                fontFamily: 'Georgia, serif',
                lineHeight: 0.5,
                display: 'block',
                marginBottom: 12,
              }}
            >
              "
            </span>

            <AnimatedText
              text={testimonialQuote}
              animationType={textAnimation}
              startFrame={QUOTE_START}
              color="rgba(255,255,255,0.9)"
              fontSize={36}
              fontWeight={400}
              fontFamily={fontFamily}
              textAlign="left"
              maxWidth={660}
              wordDelay={textAnimation === 'typewriter' ? 4 : 3}
              charsPerSecond={40}
            />

            {/* Customer Name - only shown if customerName is provided */}
            {customerName && customerName.trim() !== '' && (
              <div
                style={{
                  opacity: nameOpacity,
                  marginTop: 14,
                }}
              >
                <AnimatedText
                  text={`- ${customerName}`}
                  animationType={textAnimation}
                  startFrame={NAME_START}
                  color="rgba(255,255,255,0.6)"
                  fontSize={24}
                  fontWeight={400}
                  fontFamily={fontFamily}
                  textAlign="left"
                  wordDelay={4}
                />
              </div>
            )}
          </div>
        )}

        {/* Star Rating with sparkle accent
            Quality threshold: Only show if rating >= 4.0 AND reviewCount >= 5
            This prevents displaying inflated or statistically insignificant ratings */}
        {rating !== undefined && rating >= 4.0 && reviewCount !== undefined && reviewCount >= 5 && (
          <div style={{ position: 'relative' }}>
            <StarRating
              rating={rating}
              reviewCount={reviewCount}
              startFrame={RATING_START}
              filledColor="#FFD700"
              emptyColor="rgba(255,255,255,0.15)"
              textColor="#ffffff"
              starSize={36}
              animationDurationPerStar={0.1}
            />

            {/* Tiny sparkles near the stars */}
            {effects.sparkles && (
              <SparkleCluster
                centerX={50}
                centerY={50}
                radius={30}
                count={6}
                color="#FFD700"
                startFrame={RATING_START + fps * 0.5}
                seed={77777}
              />
            )}
          </div>
        )}

        {/* Discount Badge */}
        <DiscountBadge
          discountCode={discountCode}
          discountLabel={discountLabel}
          variant="green"
          showFrom={fps * 6.3}
          fontFamily={fontFamily}
          textAnimation={textAnimation}
        />
      </AbsoluteFill>
    </>
  );

  // Phase 2 content (caption overlay - no background, transparent)
  const phase2Content = captionText ? (
    <CaptionScene
      captionText={captionText}
      captionColor={captionColor}
      captionFont={captionFont}
      textAnimation={textAnimation}
      startFrame={PHASE_TRANSITION}
      gradientPreset="none"
      showSwipeUp={true}
      language={language}
    />
  ) : null;

  // Combined content for both phases (layered on top of background)
  const combinedContent = (
    <>
      {/* Phase 1 content with fade out */}
      {isPhase1 && phase1Content}
      {/* Phase 2 content with fade in */}
      {isPhase2 && phase2Content}
    </>
  );

  return (
    <TemplateBackground
      backgroundType={backgroundType}
      backgroundPalette={backgroundPalette}
      colorTheme={{
        brandColor,
        accentColor,
      }}
      effects={effects}
      fps={fps}
      rippleConfig={{ startFrame: PRODUCT_START }}
    >
      {combinedContent}
    </TemplateBackground>
  );
};
